import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { motion } from "framer-motion";
import Sidebar from "@/components/Sidebar";
import { NavLink, Link, Outlet, useNavigate, useLocation } from "react-router-dom";import { LayoutDashboard, Users, GraduationCap, ClipboardCheck, LineChart, Bell, Menu, X, Target, Briefcase, CreditCard, Crown, Rocket, Eye, Sparkles, Presentation, DollarSign, Clock, Banknote, Gauge, MessageSquare, Trophy, RotateCcw, MailCheck, CalendarDays, HelpCircle, ScrollText, UserPlus, CheckCircle2, Radio, Building2, ChevronRight, Globe, ReceiptText, BrainCircuit, Activity, LifeBuoy, Settings as SettingsIcon, Languages, Wrench, ConciergeBell } from "lucide-react";import { IdCard } from "lucide-react";import { ShieldCheck } from "lucide-react";import { Images } from "lucide-react";import { toast } from "sonner";
import { playChime } from "@/lib/chime";
import { ensurePushSubscribed } from "@/lib/push";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AriStatusPill from "@/components/AriStatusPill";
import AriOrb from "@/components/ari/AriOrb";
import { useLang } from "@/context/LanguageContext";
import { useHelp } from "@/components/HelpCenter";
import LegalGate from "@/components/LegalGate";
import PaymentProfileGate from "@/components/PaymentProfileGate";
import ErrorBoundary from "@/components/ErrorBoundary";
import { DemoConversionPrompt } from "@/components/DemoConversionPrompt";
import { AmbientBackground } from "@/components/marketing/AmbientBackground";
import { AuthModal } from "@/components/marketing/AuthModal";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const navFor = (role) => {
  const manager = [
    { group: "Overview", items: [
      { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    ] },
    { group: "Operations", items: [
      { to: "/reservations", label: "Reservations", icon: CalendarDays },
      { to: "/guest", label: "Guest Experience", icon: ConciergeBell },
      { to: "/staff/guest", label: "Guest Floor", icon: Radio },
      { to: "/reception", label: "Reception", icon: ConciergeBell },
      { to: "/rota", label: "Rota & Shifts", icon: CalendarDays },
      { to: "/attendance", label: "Attendance", icon: Clock },
      { to: "/checklists", label: "Checklists", icon: ClipboardCheck },
    ] },
    { group: "Team", items: [
      { to: "/staff", label: "Staff", icon: Users },
      { to: "/people", label: "People & Permissions", icon: ShieldCheck },
      { to: "/workforce", label: "Workforce", icon: Gauge },
      { to: "/performance", label: "Performance", icon: LineChart },
      { to: "/payroll", label: "Payroll", icon: Banknote },
    ] },
    { group: "Finance", items: [
      { to: "/receipts", label: "Receipt Intelligence", icon: ReceiptText },
    ] },
    { group: "Communications", items: [
      { to: "/messages", label: "Messages", icon: MessageSquare },
      { to: "/communication", label: "Communication", icon: Radio },
      { to: "/email-logs", label: "Email Logs", icon: MailCheck },
      { to: "/support", label: "Help & Support", icon: LifeBuoy },
    ] },
    { group: "Training", items: [
      { to: "/academy", label: "Training Academy", icon: Trophy },
    ] },
    { group: "Workshop", items: [
      { to: "/workshop", label: "Workshop", icon: Wrench },
    ] },
    { group: "Settings", items: [
      { to: "/complete-profile", label: "My Details", icon: IdCard },
      { to: "/ownership", label: "Ownership", icon: Crown },
    ] },
  ];
  const staff = [
    { group: "Overview", items: [
      { to: "/me", label: "My Space", icon: LayoutDashboard },
    ] },
    { group: "Service", items: [
      { to: "/staff/guest", label: "Guest Floor", icon: Radio },
    ] },
    { group: "My Work", items: [
      { to: "/rota", label: "My Rota", icon: CalendarDays },
      { to: "/attendance", label: "Attendance", icon: Clock },
      { to: "/checklists", label: "Checklists", icon: ClipboardCheck },
      { to: "/payroll", label: "My Pay", icon: Banknote },
      { to: "/complete-profile", label: "My Details", icon: IdCard },
    ] },
    { group: "Growth", items: [
      { to: "/academy", label: "Training Academy", icon: Trophy },
    ] },
    { group: "Support", items: [
      { to: "/messages", label: "Messages", icon: MessageSquare },
    ] },
    { group: "Workshop", items: [
      { to: "/workshop", label: "Workshop", icon: Wrench },
    ] },
  ];
  const sales = [{ group: null, items: [
    { to: "/sales", label: "Sales Dashboard", icon: LayoutDashboard },
    { to: "/leads", label: "Leads", icon: Target },
    { to: "/sales/commissions", label: "My Commissions", icon: DollarSign },
    { to: "/sales/support", label: "Support", icon: MessageSquare },
    { to: "/sales/demo-guide", label: "Demo Centre", icon: Presentation },
    { to: "/sales/materials", label: "Marketing Materials", icon: Images },
    { to: "/sales/academy", label: "Sales Academy", icon: Trophy },
    { to: "/sales/payment-profile", label: "Payment Profile", icon: CreditCard },
  ] }];
  const superAdmin = [
    { group: "Overview", items: [
      { to: "/platform", label: "Platform", icon: LayoutDashboard },
      { to: "/admin/customers", label: "Customers & Subscriptions", icon: CreditCard },
      { to: "/admin/system-health", label: "System Health", icon: Activity },
    ] },
    { group: "Sales Team", items: [
      { to: "/admin/sales-team", label: "Sales Team", icon: Users },
      { to: "/admin/support", label: "Support", icon: MessageSquare },
      { to: "/admin/recruitment", label: "Recruitment", icon: UserPlus },
      { to: "/admin/sales", label: "Sales Oversight", icon: Briefcase },
      { to: "/leads", label: "All Leads", icon: Target },
    ] },
    { group: "Finance", items: [
      { to: "/admin/commissions", label: "Commissions", icon: DollarSign },
      { to: "/admin/commission-approval", label: "Commission Approval", icon: CheckCircle2 },
      { to: "/admin/payments", label: "Payment Profiles", icon: CreditCard },
    ] },
    { group: "Enablement", items: [
      { to: "/sales/demo-guide", label: "Demo Centre", icon: Presentation },
      { to: "/sales/materials", label: "Marketing Materials", icon: Images },
      { to: "/sales/academy", label: "Sales Academy", icon: Trophy },
    ] },
    { group: "Governance", items: [
      { to: "/admin/legal", label: "Legal & Compliance", icon: ScrollText },
    ] },
  ];
  const applicant = [{ group: null, items: [
    { to: "/onboarding", label: "Onboarding", icon: Rocket },
    { to: "/sales/academy", label: "Sales Academy", icon: Trophy },
  ] }];
  if (role === "staff") return staff;
  if (role === "salesperson") return sales;
  if (role === "super_admin") return superAdmin;
  if (role === "sales_applicant") return applicant;
  if (role === "owner") {
    const m = manager.map((g) => ({ ...g, items: [...g.items] }));
    m[m.length - 1].items.push({ to: "/billing", label: "Billing", icon: CreditCard });
    return m;
  }
  return manager;
};

// Inject a live "Your Capabilities" group listing genuinely PUBLISHED Workshop capabilities so
// owners/managers/staff can open them straight from the sidebar (works in demo AND production).
const CAP_GROUP = "Your Capabilities";
function buildNav(base, caps) {
  if (!caps || !caps.length) return base;
  const capGroup = {
    group: CAP_GROUP,
    items: caps.slice(0, 12).map((c) => ({ to: `/capability/${c.id}`, label: c.name || "Capability", icon: Sparkles, testid: `nav-capability-${c.id}` })),
  };
  const out = base.map((g) => ({ ...g, items: [...g.items] }));
  const idx = out.findIndex((g) => g.group === "Workshop");
  if (idx >= 0) out.splice(idx + 1, 0, capGroup);
  else out.push(capGroup);
  return out;
}

function NotificationBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const prevUnread = useRef(null);
  const navigate = useNavigate();
  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
      const u = data.filter((i) => !i.read).length;
      if (prevUnread.current !== null && u > prevUnread.current) playChime("notification");
      prevUnread.current = u;
    } catch (e) { console.error("Failed to load notifications", e); }
  };
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, []);
  const unread = items.filter((i) => !i.read).length;
  const markAll = async () => { await api.post("/notifications/read-all"); load(); };
  const openNote = async (n) => {
    if (!n.read) { try { await api.post(`/notifications/${n.id}/read`); } catch { /* */ } }
    setOpen(false);
    if (n.link) navigate(n.link);
    else load();
  };
  return (
    <div className="relative">
      <button data-testid="notifications-button" onClick={() => setOpen(!open)} className="relative w-10 h-10 rounded-full bg-[#1A1A1D] border border-white/10 flex items-center justify-center hover:border-white/25 transition-colors">
        <Bell className="w-4 h-4 text-white/70" />
        {unread > 0 && <span data-testid="notifications-badge" className="absolute -top-1 -right-1 bg-[#D4AF37] text-[#0A0A0B] text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">{unread}</span>}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="fixed sm:absolute right-2 sm:right-0 top-16 sm:top-full sm:mt-2 w-[calc(100vw-1rem)] max-w-sm sm:w-80 max-h-[70vh] sm:max-h-96 overflow-auto lux-card z-50 p-2 shadow-2xl">
            <div className="flex items-center justify-between px-3 py-2 gap-2">
              <span className="text-xs uppercase tracking-widest text-white/50">Notifications</span>
              <button onClick={markAll} className="text-xs text-[#D4AF37] hover:underline whitespace-nowrap shrink-0">Mark all read</button>
            </div>
            {items.length === 0 && <p className="px-3 py-6 text-center text-sm text-white/40">No notifications</p>}
            {items.map((n) => (
              <button key={n.id} data-testid="notification-item" onClick={() => openNote(n)}
                className={`w-full text-left px-3 py-3 rounded-lg mb-1 transition-colors ${n.read ? "opacity-50" : "bg-[#1A1A1D]"} ${n.link ? "hover:bg-white/[0.06] cursor-pointer" : "cursor-default"}`}>
                <p className="text-sm text-white/80 break-words">{n.message}</p>
                <div className="flex items-center justify-between mt-1">
                  <p className="text-[10px] text-white/30 font-mono-lux">{new Date(n.created_at).toLocaleString()}</p>
                  {n.link && <span className="text-[10px] text-[#D4AF37] inline-flex items-center gap-0.5">View <ChevronRight className="w-3 h-3" /></span>}
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function Layout() {
  const { user, logout, exitDemo, endImpersonation, viewAsDemo, resetDemo, loadUser, exitTestWorkspace } = useAuth();
  const { lang, setLang, languages } = useLang();

  // ARI's fresh, once-per-day welcome (staff, managers and owners) — shown on the first activity of a
  // new day. The backend guarantees once per calendar day and never repeats the greeting day-to-day.
  useEffect(() => {
    if (!user || !user.restaurant_id) return;
    const greet = () => api.get(`/ari/daily-greeting?lang=${encodeURIComponent(lang || "en")}`)
      .then(({ data }) => { if (data.greeting) toast(data.greeting, { icon: <AriOrb size={18} state="insight" />, duration: 8000 }); })
      .catch(() => {});
    greet();
    ensurePushSubscribed();
    const onVis = () => { if (document.visibilityState === "visible") greet(); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);
  const { openHelp } = useHelp();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unreadMsgs, setUnreadMsgs] = useState(0);
  const [demoMembers, setDemoMembers] = useState([]);
  const [demoBusy, setDemoBusy] = useState(false);
  const [sectors, setSectors] = useState([]);
  const [currentSector, setCurrentSector] = useState("");
  const [demoCountries, setDemoCountries] = useState([]);
  const countrySeq = useRef(0);
  const countryAbort = useRef(null);
  const [currentCountry, setCurrentCountry] = useState("");
  const [workshopCaps, setWorkshopCaps] = useState([]);
  const [receptionOp, setReceptionOp] = useState(false);
  useEffect(() => {
    if (user.role !== "staff") return;
    api.get("/guest/reception/me").then((r) => setReceptionOp(!!r.data.can_operate)).catch(() => {});
  }, [user.role, user.id]);
  const navWithCaps = useMemo(() => {
    const base = navFor(user.role);
    if (user.role === "staff" && receptionOp) {
      const svc = base.find((g) => g.group === "Service");
      if (svc) svc.items = [...svc.items, { to: "/reception", label: "Reception", icon: ConciergeBell }];
    }
    return buildNav(base, workshopCaps);
  }, [user.role, workshopCaps, receptionOp]);
  // Desktop icon-rail preference, remembered per browser. Mobile always uses the full drawer.
  const [railCollapsed, setRailCollapsed] = useState(() => {
    try { return localStorage.getItem("ari_sidebar_collapsed") === "1"; } catch { return false; }
  });
  const toggleRail = useCallback(() => setRailCollapsed((v) => {
    const nv = !v;
    try { localStorage.setItem("ari_sidebar_collapsed", nv ? "1" : "0"); } catch { /* */ }
    return nv;
  }), []);
  const closeMobile = useCallback(() => setMobileOpen(false), []);
  const isDemo = user.is_demo || user.auth_provider === "demo";
  const isPublicDemo = isDemo && typeof window !== "undefined" && !!localStorage.getItem("si_public_demo");
  const isImpersonating = !!user.impersonated_by;
  const isTestWorkspace = (!!user.admin_test) || (typeof window !== "undefined" && !!localStorage.getItem("si_ws_admin"));

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!isDemo) return;
    api.get("/sales/demo/members").then((r) => setDemoMembers(r.data.members || [])).catch(() => {});
    api.get("/sales/demo/business-type").then((r) => {
      setSectors(r.data.sectors || []);
      setCurrentSector(r.data.current || "");
    }).catch(() => {});
    api.get("/sales/demo/country").then((r) => {
      setDemoCountries(r.data.countries || []);
      setCurrentCountry(r.data.current || "");
    }).catch(() => {});
  }, [isDemo, user.id]);

  // Live "Your Capabilities" sidebar group — genuinely published Workshop capabilities for this
  // tenant (demo + production). Refreshes on publish (event) and on a light poll.
  useEffect(() => {
    if (!["owner", "manager", "staff"].includes(user.role)) { setWorkshopCaps([]); return; }
    let alive = true;
    const loadCaps = () => api.get("/workshop/published")
      .then((r) => { if (alive) setWorkshopCaps(Array.isArray(r.data) ? r.data : []); })
      .catch(() => {});
    loadCaps();
    const t = setInterval(loadCaps, 20000);
    window.addEventListener("workshop:published", loadCaps);
    return () => { alive = false; clearInterval(t); window.removeEventListener("workshop:published", loadCaps); };
  }, [user.role, user.id]);


  // Prospects switch the demo's hospitality sector to see the platform adapt (Academy
  // paths, roles, terminology, AI framing). Reloads so freshly-generated data is shown.
  const doSetSector = async (key) => {
    if (!key || key === currentSector) return;
    setDemoBusy(true);
    try {
      const { data } = await api.post("/sales/demo/business-type", { restaurant_type: key });
      // The sector switch fully re-provisions the demo → a fresh demo-owner token. Swap it in
      // and reload the user in-app (no full-page reload → no flash/flip). Page fetches and the
      // sector context reconcile to the new sector automatically.
      if (data?.token) {
        localStorage.setItem("si_token", data.token);
        localStorage.removeItem("si_lang");
        await loadUser();
      }
      navigate("/", { replace: true });
    } finally { setDemoBusy(false); }
  };
  const SectorSwitch = () => (
    <div className="flex items-center gap-1.5 shrink-0" data-testid="demo-sector-switch" title="Experience the platform for a different hospitality sector">
      <Building2 className="w-3.5 h-3.5 text-[#D4AF37]/80 shrink-0" />
      <select data-testid="demo-sector-select" value={currentSector} disabled={demoBusy}
        onChange={(e) => doSetSector(e.target.value)}
        className="text-xs font-semibold uppercase tracking-wider bg-white/[0.05] text-[#F3F4F6] rounded-full px-3 py-1.5 border border-[#D4AF37]/30 focus:ring-0 focus:border-[#D4AF37] hover:border-[#D4AF37]/60 transition-colors cursor-pointer">
        {sectors.map((s) => <option key={s.key} value={s.key} className="bg-[#111113] text-[#F3F4F6]">{`Sector: ${s.label}`}</option>)}
      </select>
    </div>
  );

  // COUNTRY switch — determines the demonstrated payroll jurisdiction (framework + currency +
  // calculations). Independent from language and sector. Only payroll-ready countries are listed.
  const doSetCountry = async (cc) => {
    if (!cc || cc === currentCountry) return;
    // Latest-selection-wins: bump a sequence, abort the previous in-flight request, and ignore any
    // response that is no longer the newest selection. The backend also refuses to regress on seq.
    const seq = (countrySeq.current += 1);
    setCurrentCountry(cc); // optimistic — the selector reflects the newest choice immediately
    setDemoBusy(true);
    if (countryAbort.current) countryAbort.current.abort();
    const controller = new AbortController();
    countryAbort.current = controller;
    try {
      await api.post("/sales/demo/country", { country_code: cc, seq }, { signal: controller.signal });
      if (seq !== countrySeq.current) return; // superseded by a newer selection — ignore
      if (location.pathname === "/payroll") {
        window.dispatchEvent(new CustomEvent("demo:country-changed", { detail: { country_code: cc, seq } }));
      } else {
        navigate("/payroll", { replace: true });
      }
    } catch (e) {
      /* aborted or failed — a newer selection is (or will be) authoritative */
    } finally {
      if (seq === countrySeq.current) setDemoBusy(false);
    }
  };
  const CountrySwitch = () => (
    <div className="flex items-center gap-1.5 shrink-0" data-testid="demo-country-switch" title="Demonstrate the platform for a different country's payroll jurisdiction">
      <Globe className="w-3.5 h-3.5 text-[#D4AF37]/80 shrink-0" />
      <select data-testid="demo-country-select" value={currentCountry} disabled={demoBusy}
        onChange={(e) => doSetCountry(e.target.value)}
        className="text-xs font-semibold uppercase tracking-wider bg-white/[0.05] text-[#F3F4F6] rounded-full px-3 py-1.5 border border-[#D4AF37]/30 focus:ring-0 focus:border-[#D4AF37] hover:border-[#D4AF37]/60 transition-colors cursor-pointer">
        {demoCountries.map((c) => <option key={c.code} value={c.code} className="bg-[#111113] text-[#F3F4F6]">{`${c.flag ? c.flag + " " : ""}${c.name} · ${c.currency?.code || c.currency}`}</option>)}
      </select>
    </div>
  );

  // LANGUAGE switch — determines the interface language only. Independent from country & sector.
  const LanguageSwitch = () => (
    <div className="flex items-center gap-1.5 shrink-0" data-testid="demo-language-switch" data-no-translate title="Change the interface language — payroll jurisdiction is unaffected">
      <Languages className="w-3.5 h-3.5 text-[#D4AF37]/80 shrink-0" />
      <select data-testid="demo-language-select" value={lang} disabled={demoBusy}
        onChange={(e) => setLang(e.target.value)}
        className="text-xs font-semibold uppercase tracking-wider bg-white/[0.05] text-[#F3F4F6] rounded-full px-3 py-1.5 border border-[#D4AF37]/30 focus:ring-0 focus:border-[#D4AF37] hover:border-[#D4AF37]/60 transition-colors cursor-pointer max-w-[11rem]">
        {(languages && languages.length ? languages : [{ code: "en", name: "English" }]).map((l) => (
          <option key={l.code} value={l.code} className="bg-[#111113] text-[#F3F4F6]">{`Language: ${l.name}`}</option>
        ))}
      </select>
    </div>
  );

  const doViewAs = async (userId) => {
    if (userId === user.id) return;
    setDemoBusy(true);
    // Smooth in-app switch (no full-page reload → no flash, viewport jump or layout flip).
    // viewAsDemo swaps the token & reloads the user; the per-user language effect and each
    // page's own data fetch reconcile automatically. Nothing carries over between roles.
    try { await viewAsDemo(userId); navigate("/", { replace: true }); }
    catch { /* ignore */ }
    finally { setDemoBusy(false); }
  };

  // Public demo only: let prospects experience the platform as ANY member of the demo
  // team (management, front-of-house, kitchen, housekeeping, new starter). Switching
  // re-issues a real token for that person so every screen, the AI context, training,
  // rota, tasks and notifications reflect their perspective. NEVER rendered in live
  // production — this block is gated behind isDemo.
  const roleGroups = [
    { role: "owner", label: "Ownership" },
    { role: "manager", label: "Management" },
    { role: "staff", label: "Team" },
  ];
  const RoleSwitch = () => (
    <div className="flex items-center gap-1.5 shrink-0" data-testid="demo-role-switch" title="Experience the platform as a different member of the team">
      <Users className="w-3.5 h-3.5 text-[#D4AF37]/80 shrink-0" />
      <select data-testid="demo-role-select" value={user.id} disabled={demoBusy}
        onChange={(e) => doViewAs(e.target.value)}
        className="text-xs font-semibold uppercase tracking-wider bg-white/[0.05] text-[#F3F4F6] rounded-full px-3 py-1.5 border border-[#D4AF37]/30 focus:ring-0 focus:border-[#D4AF37] hover:border-[#D4AF37]/60 transition-colors cursor-pointer max-w-[15rem]">
        {roleGroups.map((g) => {
          const members = demoMembers.filter((m) => m.role === g.role);
          if (!members.length) return null;
          return (
            <optgroup key={g.role} label={g.label} className="bg-[#111113] text-[#F3F4F6]">
              {members.map((m) => (
                <option key={m.id} value={m.id} className="bg-[#111113] text-[#F3F4F6]">{`${m.name}${m.position ? " · " + m.position : ""}`}</option>
              ))}
            </optgroup>
          );
        })}
      </select>
    </div>
  );

  // Subtle "viewing in [Language]" indicator for the demo — instantly shows prospects that
  // the platform speaks each team member's own language. Only shown when not English.
  const langName = (languages.find((l) => l.code === lang) || {}).name;
  const LangIndicator = () => {
    if (lang === "en" || !langName) return null;
    return (
      <div data-testid="demo-lang-indicator" title={`This team member's profile is in ${langName}`}
        className="flex items-center gap-1.5 shrink-0 text-xs font-semibold uppercase tracking-wider bg-white/[0.05] text-[#F3F4F6] border border-[#D4AF37]/30 rounded-full px-3 py-1.5">
        <Globe className="w-3.5 h-3.5 shrink-0 text-[#D4AF37]/80" /> <span>Viewing in {langName}</span>
      </div>
    );
  };
  const doResetDemo = async () => {
    setDemoBusy(true);
    try { await resetDemo(); navigate("/", { replace: true }); }
    finally { setDemoBusy(false); }
  };

  const prevMsgs = useRef(-1);
  const loadUnread = () => {
    if (!["owner", "manager", "staff"].includes(user.role)) return;
    api.get("/messages/unread-count").then((r) => {
      const c = r.data.count || 0;
      if (prevMsgs.current >= 0 && c > prevMsgs.current) playChime("message");
      prevMsgs.current = c;
      setUnreadMsgs(c);
    }).catch(() => {});
  };
  useEffect(() => {
    loadUnread();
    const t = setInterval(loadUnread, 8000);
    const onRead = () => loadUnread();
    window.addEventListener("si-messages-read", onRead);
    return () => { clearInterval(t); window.removeEventListener("si-messages-read", onRead); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const doLogout = useCallback(async () => { await logout(); navigate("/login", { replace: true }); }, [logout, navigate]);
  const doExitDemo = async () => { await exitDemo(); navigate("/sales", { replace: true }); };
  const doEndImp = async () => { await endImpersonation(); navigate("/admin/sales-team", { replace: true }); };
  const doExitWorkspace = async () => { await exitTestWorkspace(); navigate("/platform", { replace: true }); };

  const emptyLead = { restaurant_name: "", contact_person: "", email: "", phone: "", notes: "" };
  const [leadOpen, setLeadOpen] = useState(false);
  const [leadForm, setLeadForm] = useState(emptyLead);
  const [savingLead, setSavingLead] = useState(false);
  const submitLead = async (e) => {
    e.preventDefault();
    if (!leadForm.restaurant_name.trim()) { toast.error("Restaurant name is required"); return; }
    setSavingLead(true);
    try {
      await api.post("/sales/demo/capture-lead", leadForm);
      toast.success("Lead captured — added to your CRM pipeline");
      setLeadForm(emptyLead);
      setLeadOpen(false);
      await doExitDemo();
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail));
    } finally {
      setSavingLead(false);
    }
  };
  const exitWithoutSaving = async () => {
    setLeadOpen(false);
    await doExitDemo();
  };

  // Public customer demo — direct Subscribe conversion (create a customer account).
  // No lead capture: marketing/Public Demo visitors are never routed into the sales lead workflow.
  const [authOpen, setAuthOpen] = useState(false);
  const doExitPublic = () => {
    localStorage.removeItem("si_token");
    localStorage.removeItem("si_public_demo");
    window.location.assign("/");
  };

  const Sid = (collapsed, showToggle, onNavigate) => (
    <Sidebar user={user} nav={navWithCaps} unreadMsgs={unreadMsgs} collapsed={collapsed}
      showToggle={showToggle} onToggle={toggleRail} onNavigate={onNavigate} onLogout={doLogout} />
  );

  return (
    <div className="min-h-screen flex bg-[#0A0A0B] relative">
      <AmbientBackground />
      <aside className={`hidden lg:block fixed inset-y-0 z-20 overflow-hidden bg-gradient-to-b from-[#232019] via-[#1C1914] to-[#15130D] shadow-[8px_0_40px_-24px_rgba(0,0,0,0.9)] transition-[width] duration-300 ease-[var(--ease-lux)] after:content-[''] after:absolute after:top-0 after:bottom-0 after:right-0 after:w-px after:bg-gradient-to-b after:from-transparent after:via-[#D4AF37]/25 after:to-transparent ${railCollapsed ? "w-[72px]" : "w-64"}`} data-testid="app-sidebar">
        {Sid(railCollapsed, true, undefined)}
      </aside>
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex" data-testid="mobile-drawer">
          <div className="w-[82vw] max-w-xs bg-gradient-to-b from-[#232019] via-[#1C1914] to-[#15130D] shadow-[8px_0_40px_-16px_rgba(0,0,0,0.9)] animate-in slide-in-from-left duration-300">{Sid(false, false, closeMobile)}</div>
          <div className="flex-1 bg-black/60 backdrop-blur-sm" onClick={closeMobile} />
        </div>
      )}
      <div className={`flex-1 min-w-0 flex flex-col min-h-screen relative z-10 transition-[margin] duration-300 ease-[var(--ease-lux)] ${railCollapsed ? "lg:ml-[72px]" : "lg:ml-64"}`}>
        {isImpersonating && (
          <div data-testid="impersonation-banner" className="bg-[#7F1D1D] text-white px-4 sm:px-8 py-2.5 flex flex-wrap items-center justify-between gap-2 sticky top-0 z-40">
            <div className="flex items-center gap-2 text-sm font-medium min-w-0">
              <Eye className="w-4 h-4 shrink-0" />
              <span className="truncate">Impersonating <strong>{user.name}</strong> — viewing as Super Admin.</span>
            </div>
            <button data-testid="return-superadmin-button" onClick={doEndImp}
              className="shrink-0 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider bg-white text-[#7F1D1D] rounded-full px-4 py-1.5 hover:bg-white/90 transition-colors">
              <X className="w-3.5 h-3.5" /> Return to Super Admin
            </button>
          </div>
        )}
        {isTestWorkspace && (
          <div data-testid="test-workspace-banner" className="px-4 sm:px-8 py-2.5 flex flex-wrap items-center justify-between gap-2 sticky top-0 z-40 border-b"
            style={{ background: "linear-gradient(90deg, rgba(212,175,55,0.16), rgba(212,175,55,0.06))", borderColor: "rgba(212,175,55,0.3)" }}>
            <div className="flex items-center gap-2 text-sm font-medium min-w-0 text-[#F6E3A6]">
              <Wrench className="w-4 h-4 shrink-0" />
              <span className="truncate">Live Customer Test Workspace — <strong>{user.restaurant || (typeof window !== "undefined" && localStorage.getItem("si_ws_name")) || "Test Kitchen"}</strong>. You're acting as a real owner.</span>
            </div>
            <button data-testid="exit-test-workspace-button" onClick={doExitWorkspace}
              className="shrink-0 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider bg-[#D4AF37] text-[#0A0A0B] rounded-full px-4 py-1.5 hover:bg-[#FBECA1] transition-colors">
              <X className="w-3.5 h-3.5" /> Exit to Admin
            </button>
          </div>
        )}
        {isPublicDemo && (
          <div data-testid="public-demo-banner"
            className="relative overflow-hidden border-b border-[#D4AF37]/25 px-4 sm:px-8 py-3 flex flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sticky top-0 z-40"
            style={{ background: "radial-gradient(120% 180% at 0% 0%, rgba(212,175,55,0.16), transparent 55%), linear-gradient(180deg,#141210,#0a0a0b)" }}>
            <div className="ambient-holo-sales" aria-hidden />
            <div className="relative flex items-center gap-2.5 min-w-0">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 shrink-0">
                <Rocket className="w-3.5 h-3.5 text-[#D4AF37]" />
              </span>
              <span className="min-w-0 leading-tight">
                <span className="block text-[10px] font-mono-lux uppercase tracking-[0.28em] text-[#D4AF37]/90">Live demo</span>
                <span className="block text-xs text-white/55">
                  <span className="sm:hidden">Sample data only</span>
                  <span className="hidden sm:inline">Exploring Admin Restaurant Intelligence — sample data only</span>
                </span>
              </span>
            </div>
            <div className="relative flex flex-wrap items-center gap-2 w-full sm:w-auto justify-start sm:justify-end">
              {SectorSwitch()}
              {CountrySwitch()}
              {RoleSwitch()}
              {LanguageSwitch()}
              <button data-testid="public-get-started-button" onClick={() => setAuthOpen(true)}
                className="get-ari-shine flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#1A1408] rounded-full px-4 py-2 shrink-0 transition-transform duration-300 hover:-translate-y-0.5"
                style={{ background: "linear-gradient(100deg,#F6E3A6,#D4AF37 55%,#E9CE7A)", boxShadow: "0 8px 24px -10px rgba(212,175,55,0.6)" }}>
                <Sparkles className="w-3.5 h-3.5 shrink-0" /> Get ARI
              </button>
              <button data-testid="public-exit-demo-button" onClick={doExitPublic}
                className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider bg-white/[0.04] text-white/80 border border-white/15 rounded-full px-3 py-2 shrink-0 hover:border-[#D4AF37]/50 hover:text-white transition-colors">
                <X className="w-3.5 h-3.5 shrink-0" /> Exit
              </button>
            </div>
          </div>
        )}
        {isPublicDemo && (
          <AuthModal open={authOpen} initialMode="register" onClose={() => setAuthOpen(false)} />
        )}
        {isDemo && !isPublicDemo && (
          <div data-testid="demo-mode-banner"
            className="relative overflow-hidden border-b border-[#D4AF37]/25 px-4 sm:px-8 py-3 flex flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sticky top-0 z-40"
            style={{ background: "radial-gradient(120% 180% at 0% 0%, rgba(212,175,55,0.16), transparent 55%), linear-gradient(180deg,#141210,#0a0a0b)" }}>
            <div className="ambient-holo-sales" aria-hidden />
            <div className="relative flex items-center gap-2.5 min-w-0">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 shrink-0">
                <Rocket className="w-3.5 h-3.5 text-[#D4AF37]" />
              </span>
              <span className="min-w-0 leading-tight">
                <span className="block text-[10px] font-mono-lux uppercase tracking-[0.28em] text-[#D4AF37]/90">Demo Mode</span>
                <span className="block text-xs text-white/55 truncate">Viewing as <strong className="text-white/75 font-semibold">{user.name}</strong> ({user.role}) — sample data only</span>
              </span>
            </div>
            <div className="relative flex flex-wrap items-center gap-2 w-full sm:w-auto justify-start sm:justify-end">
              {SectorSwitch()}
              {CountrySwitch()}
              {RoleSwitch()}
              {LanguageSwitch()}
              <button data-testid="reset-demo-button" onClick={doResetDemo} disabled={demoBusy}
                className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider bg-white/[0.04] text-white/80 border border-white/15 rounded-full px-3 py-2 shrink-0 hover:border-[#D4AF37]/50 hover:text-white transition-colors disabled:opacity-50">
                <RotateCcw className="w-3.5 h-3.5 shrink-0" /> Reset
              </button>
              <button data-testid="exit-demo-button" onClick={() => setLeadOpen(true)}
                className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider bg-white/[0.04] text-white/80 border border-white/15 rounded-full px-4 py-2 shrink-0 hover:border-[#D4AF37]/50 hover:text-white transition-colors">
                <X className="w-3.5 h-3.5 shrink-0" /> Exit Demo
              </button>
            </div>
          </div>
        )}
        {isDemo && !isPublicDemo && (
          <Dialog open={leadOpen} onOpenChange={setLeadOpen}>
            <DialogContent className="bg-[#111113] border-white/10 text-white" data-testid="book-demo-dialog">
              <DialogHeader>
                <DialogTitle className="font-serif-lux text-2xl font-light">Exit Demo — Capture this prospect?</DialogTitle>
                <DialogDescription className="text-white/50">Before you leave, capture this prospect as a lead — it flows into your CRM pipeline attributed to you. Or exit without saving.</DialogDescription>
              </DialogHeader>
              <div className="rounded-lg border border-[#D4AF37]/30 bg-[#D4AF37]/[0.06] p-3 flex items-start gap-2" data-testid="demo-ai-coach-callout">
                <Sparkles className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" />
                <p className="text-xs text-white/60">Every subscription includes the <span className="text-[#D4AF37]">Intelligence Coach</span> — remind them it's built in, not an add-on.</p>
              </div>
              <form onSubmit={submitLead} className="space-y-3">
                <Input data-testid="lead-restaurant-name" placeholder="Restaurant name *" value={leadForm.restaurant_name}
                  onChange={(e) => setLeadForm({ ...leadForm, restaurant_name: e.target.value })} className="bg-[#1A1A1D] border-white/10" />
                <Input data-testid="lead-contact-person" placeholder="Contact person" value={leadForm.contact_person}
                  onChange={(e) => setLeadForm({ ...leadForm, contact_person: e.target.value })} className="bg-[#1A1A1D] border-white/10" />
                <div className="grid grid-cols-2 gap-3">
                  <Input data-testid="lead-email" type="email" placeholder="Email" value={leadForm.email}
                    onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })} className="bg-[#1A1A1D] border-white/10" />
                  <Input data-testid="lead-phone" placeholder="Phone" value={leadForm.phone}
                    onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })} className="bg-[#1A1A1D] border-white/10" />
                </div>
                <Textarea data-testid="lead-notes" placeholder="Notes" value={leadForm.notes}
                  onChange={(e) => setLeadForm({ ...leadForm, notes: e.target.value })} className="bg-[#1A1A1D] border-white/10" />
                <DialogFooter className="gap-2">
                  <button type="button" onClick={exitWithoutSaving} data-testid="exit-without-saving-button"
                    className="rounded-lg px-5 py-2.5 text-sm text-white/60 hover:text-white border border-white/15">
                    Exit without saving
                  </button>
                  <button type="submit" disabled={savingLead} data-testid="lead-submit-button"
                    className="gold-btn rounded-lg px-5 py-2.5 text-sm disabled:opacity-50">
                    {savingLead ? "Saving…" : "Add to CRM & Exit"}
                  </button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
        <header className="glass sticky top-0 z-30 px-5 sm:px-8 lg:px-10 h-[76px] sm:h-20 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button className="lg:hidden shrink-0 text-white/70 hover:text-white transition-colors duration-300" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-button">
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.22em] text-white/35">Welcome</p>
              <p className="font-serif-lux text-xl leading-tight truncate" data-testid="header-username">{user.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 sm:gap-4 shrink-0">
            {["owner", "manager", "staff"].includes(user.role) && (
              <button onClick={() => openHelp()} data-testid="help-button"
                className="flex items-center gap-1.5 text-sm text-white/60 hover:text-[#D4AF37] transition-colors duration-300">
                <HelpCircle className="w-5 h-5" /><span className="hidden sm:inline">Help</span>
              </button>
            )}
            <NotificationBell />
            {["owner", "manager", "staff"].includes(user.role) && <AriStatusPill />}
            {["owner", "manager", "staff"].includes(user.role) && (
              <button onClick={() => navigate("/settings")} data-testid="header-settings-button" aria-label="Settings"
                className="w-10 h-10 rounded-full bg-[#1A1A1D] border border-white/10 flex items-center justify-center text-white/70 hover:text-[#D4AF37] hover:border-white/25 transition-colors">
                <SettingsIcon className="w-4 h-4" />
              </button>
            )}
            <div className="w-10 h-10 rounded-full bg-gradient-to-b from-[#1D1D20] to-[#141416] ring-1 ring-[#D4AF37]/30 shadow-[0_0_18px_-6px_rgba(212,175,55,0.5)] flex items-center justify-center text-[#D4AF37] font-serif-lux text-lg">
              {user.name?.[0]?.toUpperCase()}
            </div>
          </div>
        </header>
        <main className="pm-scroll flex-1 min-w-0 p-5 sm:p-8 lg:p-10">
          <ErrorBoundary resetKey={location.pathname}>
            <motion.div key={location.pathname} className="h-full"
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}>
              <PaymentProfileGate />
              <Outlet />
            </motion.div>
          </ErrorBoundary>
        </main>
        {isDemo && <DemoConversionPrompt />}
      </div>
      <LegalGate />
    </div>
  );
}
